import React, { useEffect, useRef, useState } from 'react';
import api from '../../api/api';
import ItemQuantityField from '../../components/ItemQuantityField';
import { openAndPrintHtml, renderAndPrintInWindow } from '../../utils/printDocument';
import AuthorizedApiImage from '../../components/AuthorizedApiImage';
import { resolveApiUrl } from '../../api/api';

// ── Types ────────────────────────────────────────────────────────────────
interface KWItem { id: number; code: string; name: string; photoUrl?: string; notes: string; totalReceived: number; totalWrittenOff: number; balance: number; }
interface KWReceiptList { id: number; date: string; supplierName: string; invoiceNumber: string; notes: string; totalAmount: number; itemCount: number; status: string; postedAtUtc?: string | null; postedByUsername?: string | null; cancellationReason?: string | null; }
interface KWReceiptItemDto { id: number; kitchenwareItemId: number; itemName: string; quantity: number; unitPrice: number; }
interface KWReceiptDetail { id: number; date: string; supplierName: string; invoiceNumber: string; notes: string; totalAmount: number; status: string; postedAtUtc?: string | null; postedByUsername?: string | null; cancellationReason?: string | null; items: KWReceiptItemDto[]; }
interface KWWriteOffList { id: number; date: string; reason: string; notes: string; itemCount: number; status: string; postedAtUtc?: string | null; postedByUsername?: string | null; cancellationReason?: string | null; }
interface KWWriteOffItemDto { id: number; kitchenwareItemId: number; itemName: string; quantity: number; notes: string; }
interface KWWriteOffDetail { id: number; date: string; reason: string; notes: string; status: string; postedAtUtc?: string | null; postedByUsername?: string | null; cancellationReason?: string | null; items: KWWriteOffItemDto[]; }

interface KWRevisionList { id: number; name: string; date: string; notes: string; status: string; createdAt: string; itemCount: number; }
interface KWRevisionItemDto { id: number; kitchenwareItemId: number; itemCode: string; itemName: string; photoUrl?: string; expectedQuantity: number; actualQuantity: number; discrepancy: number; notes: string; }
interface KWRevisionDetail { id: number; name: string; date: string; notes: string; status: string; createdAt: string; items: KWRevisionItemDto[]; }

type Tab = 'catalog' | 'receipts' | 'writeoffs' | 'revisions';

type ButtonTone = 'success' | 'info' | 'danger' | 'warning' | 'muted' | 'primary' | 'accent';
const btn = (tone: ButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: tone === 'primary' ? 'var(--crm-navbar-dropdown-bg)' : tone === 'muted' ? 'var(--crm-surface-alt)' : `var(--crm-${tone === 'accent' ? 'accent' : `${tone}-bg`})`,
  color: tone === 'primary' ? 'var(--crm-primary-contrast)' : tone === 'muted' ? 'var(--crm-text-muted)' : tone === 'accent' ? 'var(--crm-accent-contrast)' : `var(--crm-${tone})`,
  border: `1px solid var(--crm-${tone === 'primary' || tone === 'muted' ? 'border' : tone === 'accent' ? 'accent' : tone})`,
  borderRadius: 'var(--crm-radius-md)',
  cursor: 'pointer',
  padding: '8px 12px',
  ...extra,
});
const thStyle: React.CSSProperties = { padding: '8px', textAlign: 'left', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-border)' };
const tdStyle: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--crm-table-row-border)' };

// ── Main Page ─────────────────────────────────────────────────────────────
const KitchenwarePage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('catalog');
  return (
    <div className="page-shell page-shell--wide" style={{ maxWidth: '1500px' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>Кухонний Посуд</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {([['catalog', 'Каталог посуду'], ['receipts', 'Прихідні накладні'], ['writeoffs', 'Списання'], ['revisions', 'Ревізія']] as [Tab, string][]).map(([t, label]) => (
          <button key={t} style={btn(tab === t ? 'accent' : 'muted')} onClick={() => setTab(t)}>{label}</button>
        ))}
      </div>
      {tab === 'catalog' && <CatalogTab />}
      {tab === 'receipts' && <ReceiptsTab />}
      {tab === 'writeoffs' && <WriteOffsTab />}
      {tab === 'revisions' && <RevisionsTab />}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// CATALOG TAB
// ══════════════════════════════════════════════════════════════════════════
const CatalogTab: React.FC = () => {
  const [items, setItems] = useState<KWItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // form
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoading(true);
    api.get<KWItem[]>('/kitchenware/items').then(r => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditId(null); setCode(''); setName(''); setNotes(''); setPhotoFile(null); setPhotoPreview('');
    setModalOpen(true);
  };

  const openEdit = (item: KWItem) => {
    setEditId(item.id); setCode(item.code || ''); setName(item.name); setNotes(item.notes);
    setPhotoFile(null); setPhotoPreview(item.photoUrl || '');
    setModalOpen(true);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      let savedId = editId;
      if (editId) {
        await api.put(`/kitchenware/items/${editId}`, { code, name, price: 0, notes });
      } else {
        const r = await api.post<{ id: number }>('/kitchenware/items', { code, name, price: 0, notes });
        savedId = r.data.id;
      }
      if (photoFile && savedId) {
        const fd = new FormData();
        fd.append('photo', photoFile);
        await api.post(`/kitchenware/items/${savedId}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setModalOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Видалити позицію посуду?')) return;
    await api.delete(`/kitchenware/items/${id}`);
    load();
  };

  const handlePrint = () => {
    const filtered = items.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()) || (i.code || '').toLowerCase().includes(search.toLowerCase()));
    const rows = filtered.map(i => `
      <div style="border:1px solid #ccc;border-radius:6px;padding:10px;page-break-inside:avoid;">
        ${i.photoUrl
          ? `<img src="${resolveApiUrl(i.photoUrl)}" style="width:100%;height:90px;object-fit:contain;margin-bottom:6px;" />`
          : `<div style="width:100%;height:90px;background:#f5f5f5;display:flex;align-items:center;justify-content:center;color:#aaa;font-size:12px;margin-bottom:6px;">Фото відсутнє</div>`
        }
        <div style="font-weight:700;font-size:13px;margin-bottom:2px;">${i.code ? `[${i.code}] ` : ''}${i.name}</div>
        <div style="font-size:11px;color:#666;margin-bottom:6px;">Залишок: ${i.balance.toFixed(2)} шт</div>
        <div style="border-top:1px solid #ccc;padding-top:6px;font-size:12px;">
          Пораховано: <span style="display:inline-block;width:60px;border-bottom:1px solid #999;">&nbsp;</span> шт
        </div>
      </div>`).join('');
    void printKitchenwareHtml(`
      <html><head><title>Бланк ревізії посуду</title>
      <style>
        body{font-family:Arial;padding:20px;color:#2c3e50}
        h2,p{text-align:center}
        .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
        @media print{body{padding:10px}.grid{grid-template-columns:repeat(3,1fr)}}
      </style></head><body>
      <h2>Бланк ревізії кухонного посуду</h2>
      <p>Дата: ___________&nbsp;&nbsp;&nbsp;Відповідальний: ___________________________</p>
      <div class="grid">${rows}</div>
      </body></html>`, { windowFeatures: 'width=1100,height=800' });
  };

  const printKitchenwareHtml = async (
    html: string,
    options: { windowFeatures?: string } = {}
  ) => {
    const printWindow = window.open('', '_blank', options.windowFeatures);
    if (!printWindow) {
      window.alert('Браузер заблокував вікно друку. Дозвольте спливаючі вікна та спробуйте ще раз.');
      return;
    }
    try {
      const photoIds = Array.from(html.matchAll(/src="[^"]*\/kitchenware\/items\/(\d+)\/photo"/g))
        .map((match) => Number(match[1]));
      for (const photoId of Array.from(new Set(photoIds))) {
        const photoPath = `/kitchenware/items/${photoId}/photo`;
        const { data } = await api.get<Blob>(photoPath, { responseType: 'blob' });
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => typeof reader.result === 'string'
            ? resolve(reader.result)
            : reject(new Error('Unable to read kitchenware photo.'));
          reader.onerror = () => reject(reader.error ?? new Error('Unable to read kitchenware photo.'));
          reader.readAsDataURL(data);
        });
        html = html.replaceAll(`src="${resolveApiUrl(photoPath)}"`, `src="${dataUrl}"`);
      }
      await renderAndPrintInWindow(printWindow, html);
    } catch (error) {
      if (!printWindow.closed) printWindow.close();
      window.alert(error instanceof Error ? error.message : 'Не вдалося завантажити фото для друку.');
    }
  };

  const filtered = items.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()) || (i.code || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="crm-mobile-mobile-row" style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Пошук за назвою або кодом..." style={{ padding: '8px', flex: 1, maxWidth: 300 }} />
        <button onClick={openNew} style={btn('success')}>+ Додати позицію</button>
        <button onClick={handlePrint} style={btn('muted')}>🖨 Друк бланку ревізії</button>
      </div>

      {loading ? <p>Завантаження...</p> : (
        <div className="crm-mobile-table-scroll crm-mobile-table-scroll--wide" style={{ background: 'var(--crm-surface)', boxShadow: 'var(--crm-shadow-sm)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Фото</th>
                <th style={thStyle}>Код</th>
                <th style={thStyle}>Назва</th>
                <th style={thStyle}>Опис</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Прихід</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Списано</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Залишок</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={8} style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>{loading ? 'Завантаження...' : 'Порожньо'}</td></tr>}
              {filtered.map(item => (
                <tr key={item.id}>
                  <td style={tdStyle}>
                    {item.photoUrl
                      ? <AuthorizedApiImage src={item.photoUrl} alt={item.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
                      : <div style={{ width: 48, height: 48, background: 'var(--crm-surface-alt)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--crm-text-muted)' }}>Фото</div>
                    }
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--crm-text-muted)', fontSize: 13 }}>{item.code || '—'}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{item.name}</td>
                  <td style={{ ...tdStyle, color: 'var(--crm-text-muted)', fontSize: 13 }}>{item.notes || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{item.totalReceived.toFixed(2)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{item.totalWrittenOff.toFixed(2)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: item.balance < 0 ? 'var(--crm-danger)' : 'var(--crm-success)' }}>{item.balance.toFixed(2)}</td>
                  <td style={tdStyle}>
                    <button onClick={() => openEdit(item)} style={btn('warning', { padding: '4px 10px', fontSize: 13 })}>✏</button>
                    <button onClick={() => handleDelete(item.id)} style={btn('danger', { padding: '4px 10px', fontSize: 13, marginLeft: 4 })}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {modalOpen && (
        <div className="kitchenware-edit-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={() => setModalOpen(false)}>
          <div className="kitchenware-edit-modal" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', borderRadius: 12, padding: 28, width: '100%', maxWidth: 440, boxShadow: 'var(--crm-shadow-sm)' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18 }}>{editId ? 'Редагувати позицію' : 'Нова позиція посуду'}</h3>

            {/* Photo upload */}
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <div onClick={() => photoInputRef.current?.click()} style={{ cursor: 'pointer', width: 140, height: 140, borderRadius: 8, border: '2px dashed var(--crm-border)', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: 'var(--crm-surface-alt)' }}>
                {photoPreview
                  ? <AuthorizedApiImage src={photoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ color: 'var(--crm-text-muted)', fontSize: 13 }}>📷 Фото</span>
                }
              </div>
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--crm-text-muted)' }}>Натисніть щоб вибрати фото</div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Код товару</label>
              <input value={code} onChange={e => setCode(e.target.value)} placeholder="Наприклад, K-001"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--crm-border)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Назва *</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Назва посуду" autoFocus
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--crm-border)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Опис / Примітки</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Необов'язково..."
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--crm-border)', borderRadius: 6, fontSize: 14, resize: 'vertical', boxSizing: 'border-box', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSave} disabled={saving || !name.trim()} style={btn('success', { flex: 1, fontSize: 15, padding: '10px', borderRadius: 8 })}>
                {saving ? 'Збереження...' : 'Зберегти'}
              </button>
              <button onClick={() => setModalOpen(false)} style={btn('muted', { flex: 1, fontSize: 15, padding: '10px', borderRadius: 8 })}>Скасувати</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// RECEIPTS TAB  (pattern: InventoryReceiptsPage)
// ══════════════════════════════════════════════════════════════════════════
type ReceiptRow = { key: string; itemId: string; quantity: string; unitPrice: string; };
const emptyReceiptRow = (): ReceiptRow => ({ key: crypto.randomUUID(), itemId: '', quantity: '', unitPrice: '' });

const ReceiptsTab: React.FC = () => {
  const [items, setItems] = useState<KWItem[]>([]);
  const [receipts, setReceipts] = useState<KWReceiptList[]>([]);
  const [selected, setSelected] = useState<KWReceiptDetail | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [supplier, setSupplier] = useState('');
  const [invoice, setInvoice] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<ReceiptRow[]>([emptyReceiptRow()]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ri, rr] = await Promise.all([
        api.get<KWItem[]>('/kitchenware/items'),
        api.get<KWReceiptList[]>('/kitchenware/receipts'),
      ]);
      setItems(ri.data);
      setReceipts(rr.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const openDetail = async (id: number) => {
    const r = await api.get<KWReceiptDetail>(`/kitchenware/receipts/${id}`);
    setSelected(r.data);
    setShowForm(false);
  };

  const startNew = () => {
    setEditingId(null); setDate(new Date().toISOString().slice(0, 10));
    setSupplier(''); setInvoice(''); setNotes(''); setRows([emptyReceiptRow()]);
    setSelected(null); setShowForm(true);
  };

  const startEdit = async (r: KWReceiptDetail) => {
    setEditingId(r.id); setDate(r.date.slice(0, 10));
    setSupplier(r.supplierName); setInvoice(r.invoiceNumber); setNotes(r.notes);
    setRows(r.items.map(i => ({ key: crypto.randomUUID(), itemId: String(i.kitchenwareItemId), quantity: String(i.quantity), unitPrice: String(i.unitPrice) })));
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      date: new Date(date).toISOString(), supplierName: supplier, invoiceNumber: invoice, notes,
      items: rows.filter(r => r.itemId).map(r => ({
        kitchenwareItemId: parseInt(r.itemId), quantity: parseFloat(r.quantity) || 0, unitPrice: parseFloat(r.unitPrice) || 0
      }))
    };
    if (editingId) await api.put(`/kitchenware/receipts/${editingId}`, payload);
    else await api.post('/kitchenware/receipts', payload);
    setShowForm(false);
    await loadAll();
    if (editingId) await openDetail(editingId);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Скасувати накладну? Вона залишиться в історії, але не впливатиме на залишки.')) return;
    const reason = window.prompt('Вкажіть причину скасування:');
    if (!reason?.trim()) return;
    await api.post(`/kitchenware/receipts/${id}/cancel`, { reason: reason.trim() });
    if (selected?.id === id) setSelected(null);
    await loadAll();
  };

  const handlePrint = () => {
    if (!selected) return;
    const rows = (selected.items || []).map(i => `
      <tr><td>${i.itemName||'—'}</td><td style="text-align:right;">${i.quantity.toFixed(2)}</td>
      <td style="text-align:right;">${i.unitPrice.toFixed(2)}</td>
      <td style="text-align:right;">${(i.quantity*i.unitPrice).toFixed(2)}</td></tr>`).join('');
    void openAndPrintHtml(`<html><head><title>Накладна посуду #${selected.id}</title>
      <style>body{font-family:Arial;padding:20px;color:#2c3e50}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px}th{background:#f5f6fa}tfoot td{font-weight:700}</style>
      </head><body>
      <h2>Накладна посуду №${selected.invoiceNumber||selected.id}</h2>
      <p>Дата: ${new Date(selected.date).toLocaleDateString('uk-UA')}</p>
      <p>Постачальник: ${selected.supplierName||'—'}</p>
      ${selected.notes?`<p>Примітки: ${selected.notes}</p>`:''}
      <table><thead><tr><th>Позиція</th><th>К-сть</th><th>Ціна/шт</th><th>Сума</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr><td colspan="3" style="text-align:right;">Разом:</td><td style="text-align:right;">${selected.totalAmount.toFixed(2)} ₴</td></tr></tfoot>
      </table></body></html>`, { windowFeatures: 'width=1100,height=800' });
  };

  const itemOptions = items.map(i => ({ id: String(i.id), name: i.name }));
  const filtered = receipts.filter(r => !search || r.invoiceNumber.toLowerCase().includes(search.toLowerCase()) || r.supplierName.toLowerCase().includes(search.toLowerCase()));
  const total = rows.reduce((s, r) => s + (parseFloat(r.quantity)||0)*(parseFloat(r.unitPrice)||0), 0);

  const updateRow = (idx: number, patch: Partial<ReceiptRow>) =>
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));

  return (
    <div className="no-print responsive-split-layout" style={{ marginTop: '4px' }}>
      {/* Left: list */}
      <div style={{ flex: '0 0 420px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Пошук..." style={{ flex: 1, padding: '8px' }} />
          <button onClick={startNew} style={btn('success')}>+ Нова</button>
        </div>
        <div style={{ maxHeight: '620px', overflowY: 'auto', background: 'var(--crm-surface)', boxShadow: 'var(--crm-shadow-sm)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>Дата</th><th style={thStyle}>Постачальник</th><th style={thStyle}>№</th><th style={{ ...thStyle, textAlign: 'right' }}>Сума</th></tr></thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} onClick={() => openDetail(r.id)} style={{ cursor: 'pointer', background: selected?.id === r.id ? 'var(--crm-info-bg)' : undefined }}>
                  <td style={tdStyle}>{new Date(r.date).toLocaleDateString('uk-UA')}</td>
                  <td style={tdStyle}>{r.supplierName||'—'}</td>
                  <td style={tdStyle}>{r.invoiceNumber||'—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{r.totalAmount.toFixed(2)} ₴</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>{loading ? 'Завантаження...' : 'Накладних немає'}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right: detail or form */}
      <div style={{ flex: 1 }}>
        {showForm && (
          <form onSubmit={handleSave} style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)' }}>
            <h3>{editingId ? 'Редагування накладної' : 'Нова накладна посуду'}</h3>
            <div className="kitchenware-form-meta" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <label>Дата:<input type="date" required value={date} onChange={e => setDate(e.target.value)} style={{ marginLeft: 6 }} /></label>
              <label>№ Накладної:<input value={invoice} onChange={e => setInvoice(e.target.value)} style={{ marginLeft: 6, width: 140 }} /></label>
              <label style={{ flex: 1 }}>Постачальник:<input value={supplier} onChange={e => setSupplier(e.target.value)} style={{ marginLeft: 6 }} /></label>
              <label style={{ flex: 1 }}>Примітка:<input value={notes} onChange={e => setNotes(e.target.value)} style={{ marginLeft: 6 }} /></label>
            </div>
            <div className="kitchenware-entry-list">
              {rows.map((r, idx) => (
                <div key={r.key} className="kitchenware-entry-row">
                  <ItemQuantityField items={itemOptions} value={r.itemId} onChange={id => updateRow(idx, { itemId: id })}
                    quantity={r.quantity} onQuantityChange={quantity => updateRow(idx, { quantity })}
                    label="Позиція посуду" quantityLabel="Кількість" unit="шт." min={0} step={0.01} quantityRequired={false}
                    placeholder="Оберіть позицію..." />
                  <label className="kitchenware-entry-price">Ціна за шт., ₴
                    <input type="number" step="0.01" min="0" value={r.unitPrice} onChange={e => updateRow(idx, { unitPrice: e.target.value })} />
                  </label>
                  <div className="kitchenware-entry-total"><span>Сума</span><strong>{((parseFloat(r.quantity)||0)*(parseFloat(r.unitPrice)||0)).toFixed(2)} ₴</strong></div>
                  <button type="button" aria-label="Видалити позицію" onClick={() => setRows(prev => prev.filter(row => row.key !== r.key))} style={btn('danger')}>Видалити</button>
                </div>
              ))}
            </div>
            <div className="kitchenware-entry-grand-total">Разом: {total.toFixed(2)} ₴</div>
            <button type="button" onClick={() => setRows(prev => [...prev, emptyReceiptRow()])} style={btn('info', { marginBottom: 12, borderRadius: '999px' })}>+ Рядок</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" style={btn('success', { borderRadius: '999px' })}>Зберегти</button>
              <button type="button" onClick={() => setShowForm(false)} style={btn('muted', { borderRadius: '999px' })}>Скасувати</button>
            </div>
          </form>
        )}
        {selected && !showForm && (
          <div style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Накладна №{selected.invoiceNumber||selected.id}</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {selected.status === 'Draft' && <button onClick={() => startEdit(selected)} style={btn('warning')}>Редагувати</button>}
                {selected.status !== 'Cancelled' && <button onClick={() => handleDelete(selected.id)} style={btn('danger')}>Скасувати</button>}
                <button onClick={handlePrint} style={btn('muted')}>Друк</button>
              </div>
            </div>
            <p>Дата: {new Date(selected.date).toLocaleDateString('uk-UA')} &nbsp; Статус: {selected.status === 'Cancelled' ? 'Скасовано' : 'Проведено'}{selected.postedAtUtc ? ` · Проведено ${new Date(selected.postedAtUtc).toLocaleString('uk-UA')} · ${selected.postedByUsername || 'історичний запис'}` : ''}</p>
            {selected.status !== 'Draft' && selected.status !== 'Cancelled' && <p style={{ color: 'var(--crm-text-muted)' }}>Проведений документ незмінний. Для виправлення скасуйте його та створіть новий.</p>}
            {selected.status === 'Cancelled' && selected.cancellationReason && <p style={{ color: 'var(--crm-danger)' }}>Причина скасування: {selected.cancellationReason}</p>}
            <p>Постачальник: {selected.supplierName||'—'}</p>
            {selected.notes && <p>Примітка: {selected.notes}</p>}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
              <thead><tr><th style={thStyle}>Позиція</th><th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th><th style={{ ...thStyle, textAlign: 'right' }}>Ціна/шт</th><th style={{ ...thStyle, textAlign: 'right' }}>Сума</th></tr></thead>
              <tbody>
                {(selected.items||[]).map(i => <tr key={i.id}>
                  <td style={tdStyle}>{i.itemName||'—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{i.quantity.toFixed(2)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{i.unitPrice.toFixed(2)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{(i.quantity*i.unitPrice).toFixed(2)}</td>
                </tr>)}
              </tbody>
              <tfoot><tr><td colSpan={3} style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>Разом:</td><td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{selected.totalAmount.toFixed(2)} ₴</td></tr></tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// WRITE-OFFS TAB  (pattern: InventoryWriteOffOpsPage)
// ══════════════════════════════════════════════════════════════════════════
type WriteOffRow = { key: string; itemId: string; quantity: string; notes: string; };
const emptyWORow = (): WriteOffRow => ({ key: crypto.randomUUID(), itemId: '', quantity: '', notes: '' });

const WriteOffsTab: React.FC = () => {
  const [items, setItems] = useState<KWItem[]>([]);
  const [writeOffs, setWriteOffs] = useState<KWWriteOffList[]>([]);
  const [selected, setSelected] = useState<KWWriteOffDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<WriteOffRow[]>([emptyWORow()]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ri, rw] = await Promise.all([
        api.get<KWItem[]>('/kitchenware/items'),
        api.get<KWWriteOffList[]>('/kitchenware/write-offs'),
      ]);
      setItems(ri.data);
      setWriteOffs(rw.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const openDetail = async (id: number) => {
    const r = await api.get<KWWriteOffDetail>(`/kitchenware/write-offs/${id}`);
    setSelected(r.data); setShowForm(false);
  };

  const resetForm = () => {
    setDate(new Date().toISOString().slice(0, 10)); setReason(''); setNotes(''); setRows([emptyWORow()]); setEditingId(null);
  };

  const startEdit = async (d: KWWriteOffDetail) => {
    setEditingId(d.id); setDate(d.date.slice(0, 10)); setReason(d.reason); setNotes(d.notes);
    setRows(d.items.map(i => ({ key: crypto.randomUUID(), itemId: String(i.kitchenwareItemId), quantity: String(i.quantity), notes: i.notes })));
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      date: new Date(date).toISOString(), reason, notes,
      items: rows.filter(r => r.itemId).map(r => ({
        kitchenwareItemId: parseInt(r.itemId), quantity: parseFloat(r.quantity)||0, notes: r.notes
      }))
    };
    if (editingId) await api.put(`/kitchenware/write-offs/${editingId}`, payload);
    else await api.post('/kitchenware/write-offs', payload);
    setShowForm(false); resetForm();
    await loadAll();
    if (editingId) await openDetail(editingId);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Скасувати списання? Воно залишиться в історії, але не впливатиме на залишки.')) return;
    const reason = window.prompt('Вкажіть причину скасування:');
    if (!reason?.trim()) return;
    await api.post(`/kitchenware/write-offs/${id}/cancel`, { reason: reason.trim() });
    if (selected?.id === id) setSelected(null);
    await loadAll();
  };

  const handlePrint = () => {
    if (!selected) return;
    const rowsHtml = (selected.items||[]).map(i => `
      <tr><td>${i.itemName||'—'}</td><td style="text-align:right;">${i.quantity.toFixed(2)}</td><td>${i.notes||'—'}</td></tr>`).join('');
    void openAndPrintHtml(`<html><head><title>Списання посуду #${selected.id}</title>
      <style>body{font-family:Arial;padding:20px;color:#2c3e50}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px}th{background:#f5f6fa}</style>
      </head><body>
      <h2>Списання посуду #${selected.id}</h2>
      <p>Дата: ${new Date(selected.date).toLocaleDateString('uk-UA')}</p>
      <p>Причина: ${selected.reason||'—'}</p>
      ${selected.notes?`<p>Примітка: ${selected.notes}</p>`:''}
      <table><thead><tr><th>Позиція</th><th>К-сть</th><th>Примітка</th></tr></thead>
      <tbody>${rowsHtml}</tbody></table>
      </body></html>`, { windowFeatures: 'width=1000,height=700' });
  };

  const itemOptions = items.map(i => ({ id: String(i.id), name: i.name }));
  const updateRow = (idx: number, patch: Partial<WriteOffRow>) =>
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));

  return (
    <div className="no-print responsive-split-layout" style={{ marginTop: '4px' }}>
      {/* Left: list */}
      <div style={{ flex: '0 0 420px' }}>
        <div style={{ marginBottom: 12 }}>
          <button onClick={() => { resetForm(); setSelected(null); setShowForm(true); }} style={btn('success', { width: '100%' })}>+ Нове списання</button>
        </div>
        <div style={{ maxHeight: '620px', overflowY: 'auto', background: 'var(--crm-surface)', boxShadow: 'var(--crm-shadow-sm)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>Дата</th><th style={thStyle}>Причина</th><th style={thStyle}>Позицій</th></tr></thead>
            <tbody>
              {writeOffs.map(w => (
                <tr key={w.id} onClick={() => openDetail(w.id)} style={{ cursor: 'pointer', background: selected?.id === w.id ? 'var(--crm-danger-bg)' : undefined }}>
                  <td style={tdStyle}>{new Date(w.date).toLocaleDateString('uk-UA')}</td>
                  <td style={tdStyle}>{w.reason||'—'}</td>
                  <td style={tdStyle}>{w.itemCount}</td>
                </tr>
              ))}
              {writeOffs.length === 0 && <tr><td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>{loading ? 'Завантаження...' : 'Даних немає'}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right: form or detail */}
      <div style={{ flex: 1 }}>
        {showForm && (
          <form onSubmit={handleSave} style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: 16 }}>
            <h3>{editingId ? 'Редагування списання' : 'Нове списання посуду'}</h3>
            <div className="kitchenware-form-meta" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <label>Дата:<input type="date" required value={date} onChange={e => setDate(e.target.value)} style={{ marginLeft: 6 }} /></label>
              <label style={{ minWidth: 280 }}>Причина:<input required value={reason} onChange={e => setReason(e.target.value)} style={{ marginLeft: 6 }} /></label>
              <label style={{ flex: 1 }}>Примітка:<input value={notes} onChange={e => setNotes(e.target.value)} style={{ marginLeft: 6 }} /></label>
            </div>
            <div className="kitchenware-entry-list">
              {rows.map((r, idx) => (
                <div key={r.key} className="kitchenware-entry-row kitchenware-entry-row--writeoff">
                  <ItemQuantityField items={itemOptions} value={r.itemId} onChange={id => updateRow(idx, { itemId: id })}
                    quantity={r.quantity} onQuantityChange={quantity => updateRow(idx, { quantity })}
                    label="Позиція посуду" quantityLabel="Кількість" unit="шт." min={0} step={0.01} quantityRequired={false}
                    placeholder="Оберіть позицію..." />
                  <label className="kitchenware-entry-notes">Примітка до позиції
                    <input value={r.notes} onChange={e => updateRow(idx, { notes: e.target.value })} placeholder="Напр: бій, знос..." />
                  </label>
                  <button type="button" aria-label="Видалити позицію" onClick={() => setRows(prev => prev.filter(row => row.key !== r.key))} style={btn('danger')}>Видалити</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setRows(prev => [...prev, emptyWORow()])} style={btn('info', { marginBottom: 12, borderRadius: '999px' })}>+ Рядок</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" style={btn('success', { borderRadius: '999px' })}>Зберегти</button>
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }} style={btn('muted', { borderRadius: '999px' })}>Скасувати</button>
            </div>
          </form>
        )}
        {selected && !showForm && (
          <div style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Списання #{selected.id}</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {selected.status === 'Draft' && <button onClick={() => startEdit(selected)} style={btn('warning')}>Редагувати</button>}
                {selected.status !== 'Cancelled' && <button onClick={() => handleDelete(selected.id)} style={btn('danger')}>Скасувати</button>}
                <button onClick={handlePrint} style={btn('muted')}>Друк</button>
              </div>
            </div>
            <p>Дата: {new Date(selected.date).toLocaleDateString('uk-UA')} &nbsp; Статус: {selected.status === 'Cancelled' ? 'Скасовано' : 'Проведено'}{selected.postedAtUtc ? ` · Проведено ${new Date(selected.postedAtUtc).toLocaleString('uk-UA')} · ${selected.postedByUsername || 'історичний запис'}` : ''}</p>
            {selected.status !== 'Draft' && selected.status !== 'Cancelled' && <p style={{ color: 'var(--crm-text-muted)' }}>Проведений документ незмінний. Для виправлення скасуйте його та створіть новий.</p>}
            {selected.status === 'Cancelled' && selected.cancellationReason && <p style={{ color: 'var(--crm-danger)' }}>Причина скасування: {selected.cancellationReason}</p>}
            <p>Причина: {selected.reason||'—'}</p>
            {selected.notes && <p>Примітка: {selected.notes}</p>}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
              <thead><tr><th style={thStyle}>Позиція</th><th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th><th style={thStyle}>Примітка</th></tr></thead>
              <tbody>
                {(selected.items||[]).map(i => <tr key={i.id}>
                  <td style={tdStyle}>{i.itemName||'—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{i.quantity.toFixed(2)}</td>
                  <td style={tdStyle}>{i.notes||'—'}</td>
                </tr>)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// REVISIONS TAB  (pattern: InventoryRevisionsPage, simplified for piece-counted kitchenware)
// ══════════════════════════════════════════════════════════════════════════
const RevisionsTab: React.FC = () => {
  const [revisions, setRevisions] = useState<KWRevisionList[]>([]);
  const [selected, setSelected] = useState<KWRevisionDetail | null>(null);
  const [actuals, setActuals] = useState<Record<number, string>>({});
  const [notesDraft, setNotesDraft] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [name, setName] = useState(`Ревізія посуду ${new Date().toLocaleDateString('uk-UA')}`);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');

  const loadAll = async () => {
    setLoading(true);
    try {
      const r = await api.get<KWRevisionList[]>('/kitchenware/revisions');
      setRevisions(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const openDetail = async (id: number) => {
    const r = await api.get<KWRevisionDetail>(`/kitchenware/revisions/${id}`);
    setSelected(r.data);
    setShowForm(false);
    const a: Record<number, string> = {}; const n: Record<number, string> = {};
    r.data.items.forEach(i => { a[i.id] = String(i.actualQuantity); n[i.id] = i.notes || ''; });
    setActuals(a); setNotesDraft(n);
  };

  const startNew = () => {
    setName(`Ревізія посуду ${new Date().toLocaleDateString('uk-UA')}`);
    setDate(new Date().toISOString().slice(0, 10));
    setNotes('');
    setSelected(null);
    setShowForm(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post<KWRevisionDetail>('/kitchenware/revisions', { name, date: new Date(date).toISOString(), notes });
      setShowForm(false);
      await loadAll();
      await openDetail(r.data.id);
    } finally { setSaving(false); }
  };

  const handleSaveItems = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload = selected.items.map(i => ({
        revisionItemId: i.id,
        actualQuantity: parseFloat((actuals[i.id] || '0').replace(',', '.')) || 0,
        notes: notesDraft[i.id] || '',
      }));
      await api.put(`/kitchenware/revisions/${selected.id}/items`, payload);
      await loadAll();
      await openDetail(selected.id);
    } finally { setSaving(false); }
  };

  const handleConfirm = async () => {
    if (!selected) return;
    if (!window.confirm('Підтвердити ревізію? Після підтвердження редагування буде недоступне.')) return;
    setSaving(true);
    try {
      await handleSaveItems();
      await api.post(`/kitchenware/revisions/${selected.id}/confirm`);
      await loadAll();
      await openDetail(selected.id);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Видалити ревізію?')) return;
    await api.delete(`/kitchenware/revisions/${id}`);
    if (selected?.id === id) setSelected(null);
    await loadAll();
  };

  const handlePrint = () => {
    if (!selected) return;
    const rowsHtml = selected.items.map(i => `
      <tr>
        <td>${i.itemCode ? `[${i.itemCode}] ` : ''}${i.itemName}</td>
        <td style="text-align:right;">${i.expectedQuantity.toFixed(2)}</td>
        <td style="text-align:right;border-bottom:1px solid #999;">&nbsp;</td>
        <td style="text-align:right;">&nbsp;</td>
      </tr>`).join('');
    void openAndPrintHtml(`<html><head><title>${selected.name}</title>
      <style>body{font-family:Arial;padding:20px;color:#2c3e50}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px}th{background:#f5f6fa}</style>
      </head><body>
      <h2>${selected.name}</h2>
      <p>Дата: ${new Date(selected.date).toLocaleDateString('uk-UA')}&nbsp;&nbsp;&nbsp;Відповідальний: ___________________________</p>
      ${selected.notes ? `<p>Примітка: ${selected.notes}</p>` : ''}
      <table><thead><tr><th>Позиція</th><th>Очікувано</th><th>Фактично</th><th>Розбіжність</th></tr></thead>
      <tbody>${rowsHtml}</tbody></table>
      </body></html>`, { windowFeatures: 'width=1000,height=800' });
  };

  const totalDiscrepancy = selected?.items.reduce((s, i) => s + i.discrepancy, 0) || 0;

  return (
    <div className="no-print responsive-split-layout" style={{ marginTop: '4px' }}>
      {/* Left: list */}
      <div style={{ flex: '0 0 420px' }}>
        <div style={{ marginBottom: 12 }}>
          <button onClick={startNew} style={btn('success', { width: '100%' })}>+ Нова ревізія</button>
        </div>
        <div style={{ maxHeight: '620px', overflowY: 'auto', background: 'var(--crm-surface)', boxShadow: 'var(--crm-shadow-sm)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>Дата</th><th style={thStyle}>Назва</th><th style={thStyle}>Статус</th><th style={thStyle}>Позицій</th></tr></thead>
            <tbody>
              {revisions.map(r => (
                <tr key={r.id} onClick={() => openDetail(r.id)} style={{ cursor: 'pointer', background: selected?.id === r.id ? 'var(--crm-info-bg)' : undefined }}>
                  <td style={tdStyle}>{new Date(r.date).toLocaleDateString('uk-UA')}</td>
                  <td style={tdStyle}>{r.name}</td>
                  <td style={tdStyle}>
                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 12, color: r.status === 'Confirmed' ? 'var(--crm-success)' : 'var(--crm-warning)', background: r.status === 'Confirmed' ? 'var(--crm-success-bg)' : 'var(--crm-warning-bg)', border: `1px solid ${r.status === 'Confirmed' ? 'var(--crm-success)' : 'var(--crm-warning)'}` }}>
                      {r.status === 'Confirmed' ? 'Підтверджено' : 'Чернетка'}
                    </span>
                  </td>
                  <td style={tdStyle}>{r.itemCount}</td>
                </tr>
              ))}
              {revisions.length === 0 && <tr><td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>{loading ? 'Завантаження...' : 'Ревізій ще немає'}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right: form or detail */}
      <div style={{ flex: 1 }}>
        {showForm && (
          <form onSubmit={handleCreate} style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: 16 }}>
            <h3>Нова ревізія посуду</h3>
            <div className="kitchenware-form-meta" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <label style={{ flex: 1, minWidth: 220 }}>Назва:<input required value={name} onChange={e => setName(e.target.value)} style={{ marginLeft: 6, width: 'calc(100% - 70px)' }} /></label>
              <label>Дата:<input type="date" required value={date} onChange={e => setDate(e.target.value)} style={{ marginLeft: 6 }} /></label>
              <label style={{ flex: 1 }}>Примітка:<input value={notes} onChange={e => setNotes(e.target.value)} style={{ marginLeft: 6 }} /></label>
            </div>
            <p style={{ color: 'var(--crm-text-muted)', fontSize: 13 }}>Очікувані кількості будуть автоматично розраховані з поточного залишку (прихід − списання) для кожної позиції посуду.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" disabled={saving} style={btn('success', { borderRadius: '999px' })}>{saving ? 'Створення...' : 'Створити'}</button>
              <button type="button" onClick={() => setShowForm(false)} style={btn('muted', { borderRadius: '999px' })}>Скасувати</button>
            </div>
          </form>
        )}
        {selected && !showForm && (
          <div style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ margin: 0 }}>{selected.name}</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {selected.status === 'Draft' && <button onClick={handleSaveItems} disabled={saving} style={btn('info')}>Зберегти</button>}
                {selected.status === 'Draft' && <button onClick={handleConfirm} disabled={saving} style={btn('success')}>Підтвердити</button>}
                {selected.status === 'Draft' && <button onClick={() => handleDelete(selected.id)} style={btn('danger')}>Видалити</button>}
                <button onClick={handlePrint} style={btn('muted')}>🖨 Друк</button>
              </div>
            </div>
            <p>Дата: {new Date(selected.date).toLocaleDateString('uk-UA')} &nbsp; Статус: {selected.status === 'Confirmed' ? 'Підтверджено' : 'Чернетка'}</p>
            {selected.notes && <p>Примітка: {selected.notes}</p>}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Фото</th>
                  <th style={thStyle}>Позиція</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Очікувано</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Фактично</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Розбіжність</th>
                  <th style={thStyle}>Примітка</th>
                </tr>
              </thead>
              <tbody>
                {selected.items.map(i => (
                  <tr key={i.id}>
                    <td style={tdStyle}>
                      {i.photoUrl
                        ? <AuthorizedApiImage src={i.photoUrl} alt={i.itemName} style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} />
                        : <div style={{ width: 36, height: 36, background: 'var(--crm-surface-alt)', borderRadius: 4 }} />}
                    </td>
                    <td style={tdStyle}>{i.itemCode ? <span style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>[{i.itemCode}] </span> : null}{i.itemName}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{i.expectedQuantity.toFixed(2)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {selected.status === 'Draft'
                        ? <input type="number" step="0.01" value={actuals[i.id] ?? ''} onChange={e => setActuals(prev => ({ ...prev, [i.id]: e.target.value }))} style={{ width: 80, padding: '4px 6px', textAlign: 'right' }} />
                        : i.actualQuantity.toFixed(2)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: i.discrepancy < 0 ? 'var(--crm-danger)' : i.discrepancy > 0 ? 'var(--crm-success)' : 'var(--crm-text)' }}>
                      {i.discrepancy > 0 ? '+' : ''}{i.discrepancy.toFixed(2)}
                    </td>
                    <td style={tdStyle}>
                      {selected.status === 'Draft'
                        ? <input value={notesDraft[i.id] ?? ''} onChange={e => setNotesDraft(prev => ({ ...prev, [i.id]: e.target.value }))} placeholder="Примітка..." style={{ width: '100%', padding: '4px 6px' }} />
                        : (i.notes || '—')}
                    </td>
                  </tr>
                ))}
                {selected.items.length === 0 && <tr><td colSpan={6} style={{ padding: '16px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Немає позицій посуду</td></tr>}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>Разом розбіжність:</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: totalDiscrepancy < 0 ? 'var(--crm-danger)' : totalDiscrepancy > 0 ? 'var(--crm-success)' : 'var(--crm-text)' }}>
                    {totalDiscrepancy > 0 ? '+' : ''}{totalDiscrepancy.toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {!selected && !showForm && <p style={{ color: 'var(--crm-text-muted)' }}>Оберіть ревізію зі списку або створіть нову.</p>}
      </div>
    </div>
  );
};

export default KitchenwarePage;
